import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldWarning } from "@phosphor-icons/react";

const BLANK = { pickup_authorized: false, legal_guardian: false, billing_responsible: false };

/** Imported contacts hold no permission until a director verifies them one by one. */
export const PendingPickupPanel = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [perm, setPerm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts/pending");
      setRows(data);
    } catch { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    setBusy(true);
    try {
      await api.post(`/contacts/${open.id}/verify`, { ...perm, note: "Verified from the dashboard" });
      toast.success(`${open.name} verified${perm.pickup_authorized ? " and cleared for checkout" : " without checkout permission"}`);
      setOpen(null);
      setPerm(BLANK);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (rows.length === 0) return null;

  return (
    <div className="bg-white rounded-3xl border-2 border-[#FBBF24] p-6" data-testid="pending-pickup-panel">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-2xl bg-amber-50 text-amber-700 grid place-items-center shrink-0">
          <ShieldWarning size={22} weight="duotone" />
        </span>
        <div>
          <h2 className="font-display text-xl font-semibold">Contacts awaiting verification</h2>
          <p className="text-xs font-semibold text-muted-foreground">
            {rows.length} imported adult{rows.length === 1 ? "" : "s"} — none may collect a child yet
          </p>
        </div>
      </div>
      <ul className="mt-5 space-y-2 max-h-80 overflow-y-auto">
        {rows.map((g) => (
          <li key={g.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border px-4 py-3"
              data-testid={`pending-contact-${g.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{g.name || g.email}</p>
              <p className="text-xs text-muted-foreground truncate">
                {g.relationship} for{" "}
                <Link to={`/admin/students/${g.student_id}`} className="font-bold text-[#3B82F6]">
                  {g.student_name}
                </Link>
                {g.phone ? ` · ${g.phone}` : ""}{g.email ? ` · ${g.email}` : " · no email"}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => { setOpen(g); setPerm(BLANK); }}
                    className="rounded-full font-bold shrink-0" data-testid={`verify-contact-${g.id}`}>
              Verify
            </Button>
          </li>
        ))}
      </ul>

      <Dialog open={Boolean(open)} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="verify-contact-dialog">
          <DialogHeader>
            <DialogTitle>Verify {open?.name}</DialogTitle>
            <DialogDescription>
              Imported from the roster as "{open?.relationship}" for {open?.student_name}. Grant only what you
              have actually confirmed with the family — staff cannot release a child to someone without
              checkout permission.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[["pickup_authorized", "May collect this child (checkout)"],
              ["legal_guardian", "Legal guardian"],
              ["billing_responsible", "Billing contact"]].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-4 rounded-2xl border border-border px-4 py-3">
                <span className="text-sm font-bold">{label}</span>
                <Switch checked={perm[key]} onCheckedChange={(v) => setPerm((p) => ({ ...p, [key]: v }))}
                        data-testid={`verify-${key}`} />
              </div>
            ))}
            <Button onClick={verify} disabled={busy}
                    className="w-full h-11 rounded-full font-bold bg-[#10B981] hover:bg-[#059669]"
                    data-testid="verify-submit">
              {busy ? "Saving…" : "Record verification"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
